import { useCallback, useEffect, useState } from 'react';
import { ApiError, allows, sites as loadSites, type CurrentUser, type SiteSummary } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  decideResidency,
  fetchAlerts,
  fetchAvailability,
  fetchBackups,
  fetchDevices,
  fetchHealth,
  fetchReleases,
  fetchResidency,
  fetchSyncStatus,
  deviceUiPath,
  enrollDevice,
  revokeDevice,
  rollbackRelease,
  runBackup,
  runDrill,
  saveRule,
  type AlertRule,
  type AvailabilityReport,
  type BackupRow,
  type DeviceKind,
  type DeviceRow,
  type DrillRow,
  type EnrolledDevice,
  type HealthReport,
  type OpenAlert,
  type ReleaseRow,
  type ResidencyReport,
  type SyncStatus,
} from './api';
import { opsStrings, type OpsKey, type OpsTranslate } from './strings';
import {
  Checkbox,
  ModulePage,
  Pagination,
  Table,
  SkeletonRows,
  TableSkeleton,
  TextInput,
  usePagination,
  Button,
  Field,
  Notice,
  Select,
} from '../../shell/kit';
import { useTab } from '../../shell/hooks';
import { useConfirmReason } from '../../shell/confirm';

const TABS = ['health', 'devices', 'releases', 'backups', 'residency'] as const;
type Tab = (typeof TABS)[number];

const COMPARATOR_SIGN: Readonly<Record<string, string>> = {
  gt: '>',
  lt: '<',
  eq: '=',
};

/**
 * Единица измерения выводится из имени метрики: она есть в самом имени
 * (moika_backup_age_hours), но на экране её не было, и порог читался как
 * безразмерное число.
 */
function metricUnit(metric: string): string {
  if (metric.endsWith('_hours')) return 'ч';
  if (metric.endsWith('_minutes')) return 'мин';
  if (metric.endsWith('_days')) return 'дн';
  if (metric.endsWith('_ms')) return 'мс';
  if (metric.endsWith('_bytes')) return 'байт';
  if (metric.endsWith('_ratio')) return 'доля';
  if (metric.endsWith('_total') || metric.endsWith('_max')) return 'шт';
  return '';
}

function when(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU');
}

function bytes(value: number | null): string {
  if (value === null) return '—';
  if (value < 1024) return `${value} Б`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`;
  return `${(value / 1024 / 1024).toFixed(1)} МБ`;
}

function percent(ratio: number): string {
  return `${(ratio * 100).toFixed(2)}%`;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="overflow-x-auto p-4">{children}</div>
    </section>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'bad' | 'warn' }) {
  const color = tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

// Геометрия та же, что у кнопок и полей кита: у эксплуатации свои константы,
// и до этого её кнопки были на два пикселя ниже соседних в тех же формах.
const BUTTON =
  'inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none';
const GHOST =
  'inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint';
const INPUT =
  'w-full rounded-control border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink outline-none transition placeholder:text-ink-faint hover:border-ink-faint focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-faint';
const TH = 'py-1 pr-3 text-left';

/**
 * Загрузчик раздела.
 *
 * Эффект зависит от самого `load`, а не только от счётчика перезагрузки, и это
 * не формальность. Раньше здесь стояло `[tick]` с отключённой проверкой
 * зависимостей: вкладка «Устройства» честно пересобирала свой загрузчик при
 * смене объекта (`useCallback(..., [siteId])`), хук эту новую функцию не
 * замечал, и инвентарь оставался от первого выбранного объекта. Ошибка
 * пряталась именно в хуке — на месте вызова зависимость выглядела правильной.
 *
 * Требование к вызывающему: `load` должен быть стабильной ссылкой — функцией
 * модуля либо обёрнутой в `useCallback`. Встроенная стрелка даст новый запрос
 * на каждый рендер.
 */
function useAsync<T>(load: () => Promise<T>): {
  data: T | undefined;
  error: string | undefined;
  busy: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    load()
      .then((value) => {
        if (alive) {
          setData(value);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [load, tick]);

  return { data, error, busy, reload: () => setTick((n) => n + 1) };
}

function Err({ message }: { message: string | undefined }) {
  if (!message) return null;
  return <div className="rounded bg-danger-bg p-3 text-sm text-danger">{message}</div>;
}

function HealthTab({ t }: { t: OpsTranslate }) {
  const health = useAsync<HealthReport>(fetchHealth);
  const alerts = useAsync(fetchAlerts);

  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86400_000);
  const uptime = useAsync<AvailabilityReport>(
    useCallback(() => fetchAvailability(from.toISOString(), to.toISOString()), []),
  );

  async function toggle(rule: AlertRule): Promise<void> {
    await saveRule({ metric: rule.metric, enabled: !rule.enabled });
    alerts.reload();
  }

  const statusKey = `ops.health.${health.data?.status ?? 'ok'}` as OpsKey;

  return (
    <div className="space-y-4">
      <Err message={health.error ?? alerts.error ?? uptime.error} />

      <div className="grid gap-2 sm:grid-cols-4">
        <Tile
          label={t('ops.health.status')}
          value={health.data ? t(statusKey) : t('ops.loading')}
          {...(health.data?.status === 'down'
            ? { tone: 'bad' as const }
            : health.data?.status === 'degraded'
              ? { tone: 'warn' as const }
              : {})}
        />
        <Tile label={t('ops.health.latency')} value={String(health.data?.latencyMs ?? '—')} />
        <Tile
          label={t('ops.health.availability')}
          value={uptime.data ? percent(uptime.data.ratio) : '—'}
          {...(uptime.data && !uptime.data.meetsTarget ? { tone: 'warn' as const } : {})}
        />
        <Tile label={t('ops.health.samples')} value={String(uptime.data?.samples ?? 0)} />
      </div>
      <p className="text-xs text-ink-muted">{t('ops.health.target')}</p>

      <Panel title={t('ops.health.title')}>
        {!health.data ? (
          <TableSkeleton rows={4} columns={3} label={t('ops.loading')} />
        ) : (
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.health.check')}</th>
              <th className={TH}>{t('ops.health.status')}</th>
              <th className={TH}>{t('ops.health.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {!health.data ? <SkeletonRows rows={4} columns={3} label={t('ops.loading')} /> : null}
            {(health.data?.checks ?? []).map((c) => (
              <tr key={c.name} className="border-t border-line">
                <td className="py-1 pr-3 font-mono text-xs">{c.name}</td>
                <td className={`pr-3 ${c.ok ? 'text-ok' : 'text-danger'}`}>{c.ok ? '✓' : '✗'}</td>
                <td className="text-ink-muted">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        )}
      </Panel>

      <Panel title={t('ops.alerts.title')}>
        {!alerts.data ? (
          <TableSkeleton rows={3} columns={4} label={t('ops.loading')} />
        ) : (alerts.data.open ?? []).length === 0 ? (
          <p className="text-sm text-ink-muted">{t('ops.alerts.none')}</p>
        ) : (
          <Table>
            <thead className="text-ink-muted">
              <tr>
                <th className={TH}>{t('ops.alerts.metric')}</th>
                <th className={TH}>{t('ops.alerts.scope')}</th>
                <th className={TH}>{t('ops.alerts.observed')}</th>
                <th className={TH}>{t('ops.alerts.threshold')}</th>
                <th className={TH}>{t('ops.alerts.severity')}</th>
                <th className={TH}>{t('ops.alerts.raised')}</th>
              </tr>
            </thead>
            <tbody>
              {!alerts.data ? <SkeletonRows rows={3} columns={4} label={t('ops.loading')} /> : null}
            {(alerts.data?.open ?? []).map((a: OpenAlert) => (
                <tr key={a.id} className="border-t border-line">
                  <td className="py-1 pr-3 font-mono text-xs">{a.metric}</td>
                  <td className="pr-3">{a.scope}</td>
                  <td className="pr-3 font-semibold text-danger">{a.observed}</td>
                  <td className="pr-3">{a.threshold}</td>
                  <td className="pr-3">{a.severity}</td>
                  <td>{when(a.raisedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Panel title={t('ops.alerts.rules')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.alerts.metric')}</th>
              <th className={TH}>{t('ops.alerts.threshold')}</th>
              <th className={TH}>{t('ops.alerts.severity')}</th>
              <th className={TH}>{t('ops.alerts.description')}</th>
              <th className={TH}>{t('ops.alerts.enabled')}</th>
            </tr>
          </thead>
          <tbody>
            {!alerts.data ? <SkeletonRows rows={4} columns={4} label={t('ops.loading')} /> : null}
            {(alerts.data?.rules ?? []).map((r) => (
              <tr key={r.id} className="border-t border-line">
                <td className="py-1 pr-3 font-mono text-xs">{r.metric}</td>
                <td className="pr-3">
                  {/* Само число ни о чём не говорит: «26» — это «больше 26 часов»
                      или «меньше 26»? Оператор сравнения и единица теперь рядом. */}
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-6 shrink-0 text-center text-sm text-ink-muted"
                      title={t(`ops.alerts.cmp.${r.comparator}` as OpsKey)}
                    >
                      {COMPARATOR_SIGN[r.comparator]}
                    </span>
                    <TextInput
                      type="number"
                      defaultValue={r.threshold}
                      onBlur={(e) =>
                        void saveRule({ metric: r.metric, threshold: Number(e.target.value) }).then(
                          () => alerts.reload(),
                        )
                      }
                      className="w-28 tabular"
                    />
                    <span className="shrink-0 text-xs text-ink-muted">{metricUnit(r.metric)}</span>
                  </div>
                </td>
                <td className="pr-3">{r.severity}</td>
                <td className="pr-3 text-ink-muted">{r.description}</td>
                <td>
                  <Checkbox checked={r.enabled} onChange={() => void toggle(r)} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </div>
  );
}

const DEVICE_KINDS: readonly DeviceKind[] = ['tablet', 'pos'];

/**
 * Постановка устройства на учёт.
 *
 * Живёт в «Эксплуатации», потому что здесь же лежит инвентарь: человек, который
 * ищет, где завести планшет, идёт туда, где видит список планшетов. У кассы
 * своя такая же панель в разделе «Касса и смены» — она про повседневную работу
 * кассы, а эта про парк устройств сети целиком.
 *
 * До неё планшет ставился на учёт только curl-ом: маршрут существовал с самого
 * начала, а показывать его было негде.
 */
function EnrollPanel({
  t,
  user,
  siteId,
  onEnrolled,
}: {
  readonly t: OpsTranslate;
  readonly user: CurrentUser;
  readonly siteId: string | null;
  readonly onEnrolled: () => void;
}) {
  const [sites, setSites] = useState<readonly SiteSummary[]>([]);
  const [kind, setKind] = useState<DeviceKind>('tablet');
  const [site, setSite] = useState(siteId ?? '');
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<EnrolledDevice | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const canEnroll = allows(user, 'admin.manage');

  useEffect(() => {
    if (!canEnroll) return;
    void loadSites().then((rows) => {
      setSites(rows);
      setSite((current) => current || (rows[0]?.id ?? ''));
    });
  }, [canEnroll]);

  if (!canEnroll) return null;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const device = await enrollDevice({ kind, siteId: site, label: label.trim() });
      setIssued(device);
      setCopied(false);
      setLabel('');
      onEnrolled();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={t('ops.devices.add')}>
      <Err message={error} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('ops.devices.addKind')}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as DeviceKind)}>
            {DEVICE_KINDS.map((value) => (
              <option key={value} value={value}>
                {value === 'tablet' ? t('ops.devices.tablet') : t('ops.devices.pos')}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('ops.devices.addSite')}>
          <Select value={site} onChange={(e) => setSite(e.target.value)}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.code}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('ops.devices.addLabel')} hint={t('ops.devices.addLabelHint')}>
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
      </div>

      <div className="mt-3">
        <Button tone="primary" disabled={busy || !site || label.trim().length < 2} onClick={() => void submit()}>
          {t('ops.devices.addSubmit')}
        </Button>
      </div>

      {issued ? (
        <div className="mt-4 space-y-2">
          <Notice tone="warn">{t('ops.devices.tokenOnce')}</Notice>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-ink-muted text-sm">{t('ops.devices.tokenTitle')}</span>
            <code className="rounded bg-sunken px-2 py-1 font-mono text-xs break-all">
              {issued.token}
            </code>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(issued.token);
                setCopied(true);
              }}
            >
              {copied ? t('ops.devices.tokenCopied') : t('ops.devices.tokenCopy')}
            </Button>
          </div>
          <p className="text-ink-muted text-sm">
            {t('ops.devices.openAt')}{' '}
            <code className="rounded bg-sunken px-2 py-0.5 text-xs">
              {window.location.origin}
              {deviceUiPath(kind)}
            </code>
          </p>
        </div>
      ) : null}
    </Panel>
  );
}

function DevicesTab({
  t,
  user,
  siteId,
}: {
  t: OpsTranslate;
  user: CurrentUser;
  siteId: string | null;
}) {
  const devices = useAsync<readonly DeviceRow[]>(
    useCallback(() => fetchDevices(siteId ?? undefined), [siteId]),
  );
  // Устройств в сети со временем становятся десятки: список идёт страницами.
  const paged = usePagination(devices.data ?? []);
  const sync = useAsync<SyncStatus>(
    useCallback(() => fetchSyncStatus(siteId ?? undefined), [siteId]),
  );
  const confirmReason = useConfirmReason();
  const [error, setError] = useState<string | undefined>();

  async function revoke(device: DeviceRow): Promise<void> {
    // Причина — в диалоге приложения, а не в окне браузера (QA.md, Q25).
    const reason = await confirmReason({
      title: `${t('ops.devices.revoke')}: ${device.label}`,
      reasonLabel: t('ops.devices.revokeReason'),
      confirmLabel: t('ops.devices.revoke'),
      tone: 'danger',
    });
    if (!reason) return;
    try {
      await revokeDevice(device.deviceId, reason);
      devices.reload();
      sync.reload();
      setError(undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-4">
      <Err message={error ?? devices.error ?? sync.error} />

      <div className="grid gap-2 sm:grid-cols-3">
        <Tile label={t('ops.devices.queue')} value={String(sync.data?.maxQueueDepth ?? 0)} />
        <Tile
          label={t('ops.devices.offlineCount')}
          value={String(sync.data?.offlineDevices ?? 0)}
          {...((sync.data?.offlineDevices ?? 0) > 0 ? { tone: 'warn' as const } : {})}
        />
        <Tile
          label={t('ops.devices.conflicts')}
          value={String(sync.data?.openConflicts ?? 0)}
          {...((sync.data?.openConflicts ?? 0) > 0 ? { tone: 'bad' as const } : {})}
        />
      </div>

      <EnrollPanel t={t} user={user} siteId={siteId} onEnrolled={() => devices.reload()} />

      <Panel title={t('ops.devices.title')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.devices.name')}</th>
              <th className={TH}>{t('ops.devices.id')}</th>
              <th className={TH}>{t('ops.devices.site')}</th>
              <th className={TH}>{t('ops.devices.kind')}</th>
              <th className={TH}>{t('ops.devices.version')}</th>
              <th className={TH}>{t('ops.devices.lastSeen')}</th>
              <th className={TH}>{t('ops.devices.queue')}</th>
              <th className={TH}>{t('ops.devices.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!devices.data ? <SkeletonRows rows={5} columns={8} label={t('ops.loading')} /> : null}
            {paged.rows.map((d) => (
              <tr key={d.deviceId} className="border-t border-line">
                <td className="py-1 pr-3">{d.label || '—'}</td>
                <td className="pr-3 font-mono text-xs">{d.deviceId}</td>
                <td className="pr-3">{d.siteCode}</td>
                <td className="pr-3">
                  {d.kind === 'tablet' ? t('ops.devices.tablet') : t('ops.devices.pos')}
                </td>
                <td className="pr-3">{d.appVersion ?? '—'}</td>
                <td className={`pr-3 ${d.online ? '' : 'text-warn'}`}>{when(d.lastSeenAt)}</td>
                <td className="pr-3">{d.queueDepth}</td>
                <td className={`pr-3 ${d.status === 'revoked' ? 'text-danger' : ''}`}>
                  {d.status === 'active' ? t('ops.devices.active') : t('ops.devices.revoked')}
                </td>
                <td className="text-right">
                  <button
                    className={GHOST}
                    disabled={d.status === 'revoked'}
                    onClick={() => void revoke(d)}
                  >
                    {t('ops.devices.revoke')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination paged={paged} />
      </Panel>
    </div>
  );
}

function ReleasesTab({ t }: { t: OpsTranslate }) {
  const releases = useAsync<readonly ReleaseRow[]>(fetchReleases);
  const paged = usePagination(releases.data ?? []);
  const [error, setError] = useState<string | undefined>();
  const [plan, setPlan] = useState<string | undefined>();

  /*
   * Кнопка запрашивает план, а не откатывает: процесс ядра не может сам себя
   * остановить и поднять на другом образе. Раньше она сразу ставила отметку
   * «откачен» при нетронутой схеме (REPORT №19).
   */
  async function rollback(release: ReleaseRow): Promise<void> {
    try {
      const res = await rollbackRelease(release.id);
      setPlan(
        t('ops.releases.plan', {
          from: res.plan.from,
          to: res.plan.to,
          steps: res.plan.steps,
          imageFrom: res.plan.imageFrom ?? '—',
          imageTo: res.plan.imageTo ?? '—',
          command: res.command,
        }),
      );
      releases.reload();
      setError(undefined);
    } catch (err) {
      setPlan(undefined);
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-4">
      <Err message={error ?? releases.error} />
      {plan ? <Notice tone="warn">{plan}</Notice> : null}
      <p className="text-xs text-ink-muted">{t('ops.releases.rollbackNote')}</p>

      <Panel title={t('ops.releases.title')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.releases.version')}</th>
              <th className={TH}>{t('ops.releases.channel')}</th>
              <th className={TH}>{t('ops.releases.status')}</th>
              <th className={TH}>{t('ops.releases.before')}</th>
              <th className={TH}>{t('ops.releases.after')}</th>
              <th className={TH}>{t('ops.releases.image')}</th>
              <th className={TH}>{t('ops.releases.started')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!releases.data ? <SkeletonRows rows={4} columns={7} label={t('ops.loading')} /> : null}
            {paged.rows.map((r) => (
              <tr key={r.id} className="border-t border-line">
                <td className="py-1 pr-3 font-medium">{r.version}</td>
                <td className="pr-3">{r.channel}</td>
                <td className="pr-3">
                  {r.status}
                  {r.windowOverride ? (
                    <div className="text-xs text-warn">
                      {t('ops.releases.outsideWindow', { reason: r.windowOverride })}
                    </div>
                  ) : null}
                  {r.rollbackError ? (
                    <div className="text-xs text-danger">
                      {t('ops.releases.rollbackError', { error: r.rollbackError })}
                    </div>
                  ) : null}
                </td>
                <td className="pr-3">{r.migrationBefore}</td>
                <td className="pr-3">{r.migrationAfter ?? '—'}</td>
                <td className="pr-3">{r.imageTag ?? '—'}</td>
                <td className="pr-3">{when(r.startedAt)}</td>
                <td className="text-right">
                  <button
                    className={GHOST}
                    disabled={
                      r.status !== 'completed' && r.status !== 'failed' && r.status !== 'rolling_back'
                    }
                    onClick={() => void rollback(r)}
                  >
                    {t('ops.releases.rollback')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination paged={paged} />
      </Panel>
    </div>
  );
}

function BackupsTab({ t }: { t: OpsTranslate }) {
  const data = useAsync(fetchBackups);
  // Копии снимаются ежедневно — за год их триста шестьдесят пять.
  const pagedBackups = usePagination(data.data?.backups ?? []);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const [done, setDone] = useState<string | undefined>();

  /*
   * Обе кнопки — над одним и тем же журналом, поэтому и занятость общая: пока
   * снимается копия, запускать по ней же учение бессмысленно.
   */
  async function act(run: () => Promise<string>): Promise<void> {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      setDone(await run());
      data.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function backup(): Promise<void> {
    await act(async () => {
      const row = await runBackup();
      return t('ops.backups.doneBackup', { size: bytes(row.sizeBytes) });
    });
  }

  /** Учение по конкретной копии; без аргумента — по последней. */
  async function drill(backupId?: string): Promise<void> {
    await act(async () => {
      const row = await runDrill(backupId);
      return row.status === 'passed'
        ? t('ops.backups.donePassed', {
            tables: String(row.tablesChecked ?? 0),
            rto: String(row.rtoSeconds ?? 0),
          })
        : t('ops.backups.doneFailed', { mismatches: String(row.mismatches ?? 0) });
    });
  }

  return (
    <div className="space-y-4">
      <Err message={error ?? data.error} />
      <p className="text-ink-muted text-xs">{t('ops.backups.note')}</p>

      {/*
        Состояние утилит показывается до нажатия, а не после. Раньше узнать,
        что копии на этом контуре невозможны, можно было единственным способом:
        нажать кнопку и получить красную ошибку — которая читается как поломка,
        хотя это ненастроенный стенд.
      */}
      {data.data && !data.data.toolsAvailable ? (
        <Notice tone="warn">
          <p className="font-medium">{t('ops.backups.toolsMissing')}</p>
          <p className="mt-1">{t('ops.backups.toolsMissingWhy')}</p>
          <p className="mt-1">
            {t('ops.backups.toolsPrefix')}:{' '}
            <code className="rounded bg-sunken px-1.5 py-0.5 text-xs">
              {data.data.toolPrefix || t('ops.backups.toolsNotSet')}
            </code>
          </p>
        </Notice>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          className={BUTTON}
          disabled={busy || data.data?.toolsAvailable === false}
          onClick={() => void backup()}
        >
          {busy ? t('ops.backups.running') : t('ops.backups.runBackup')}
        </button>
        <button
          className={BUTTON}
          disabled={busy || data.data?.toolsAvailable === false}
          onClick={() => void drill()}
        >
          {t('ops.backups.runDrill')}
        </button>
      </div>

      {done ? <Notice tone="ok">{done}</Notice> : null}

      {/*
        Кнопки «Восстановить поверх рабочей базы» здесь нет, и это решение, а не
        недоделка: см. строку ниже и operations/runbook-backup.md.
      */}
      <p className="text-ink-muted text-xs">{t('ops.backups.restoreNote')}</p>

      <Panel title={t('ops.backups.title')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.backups.kind')}</th>
              <th className={TH}>{t('ops.backups.status')}</th>
              <th className={TH}>{t('ops.backups.size')}</th>
              <th className={TH}>{t('ops.backups.offsite')}</th>
              <th className={TH}>{t('ops.backups.finished')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!data.data ? <SkeletonRows rows={5} columns={6} label={t('ops.loading')} /> : null}
            {pagedBackups.rows.map((b: BackupRow) => (
              <tr key={b.id} className="border-t border-line">
                <td className="py-1 pr-3">{b.kind}</td>
                <td className="pr-3">{b.status}</td>
                <td className="pr-3">{bytes(b.sizeBytes)}</td>
                <td className="pr-3">{b.offsite ? '✓' : '—'}</td>
                <td className="pr-3">{when(b.finishedAt)}</td>
                <td className="text-right">
                  <button
                    className={GHOST}
                    title={t('ops.backups.checkThisHint')}
                    disabled={busy || b.status !== 'completed' || data.data?.toolsAvailable === false}
                    onClick={() => void drill(b.id)}
                  >
                    {t('ops.backups.checkThis')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination paged={pagedBackups} />
      </Panel>

      <Panel title={t('ops.backups.drills')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.backups.status')}</th>
              <th className={TH}>{t('ops.backups.rto')}</th>
              <th className={TH}>{t('ops.backups.tables')}</th>
              <th className={TH}>{t('ops.backups.mismatches')}</th>
              <th className={TH}>{t('ops.backups.finished')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!data.data ? <SkeletonRows rows={3} columns={4} label={t('ops.loading')} /> : null}
            {(data.data?.drills ?? []).map((d: DrillRow) => (
              <tr key={d.id} className="border-t border-line">
                <td className={`py-1 pr-3 ${d.status === 'passed' ? 'text-ok' : 'text-danger'}`}>
                  {d.status === 'passed' ? t('ops.backups.passed') : t('ops.backups.failed')}
                </td>
                <td className="pr-3">{d.rtoSeconds ?? '—'}</td>
                <td className="pr-3">{d.tablesChecked}</td>
                <td className="pr-3">{d.mismatches}</td>
                <td>{when(d.finishedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </div>
  );
}

function ResidencyTab({ t }: { t: OpsTranslate }) {
  const data = useAsync<ResidencyReport>(fetchResidency);
  const confirmReason = useConfirmReason();
  const [error, setError] = useState<string | undefined>();

  async function decide(code: string, verdict: 'approved' | 'rejected'): Promise<void> {
    // Обоснование и страна — в диалогах приложения, а не в окнах браузера (QA.md, Q25).
    const evidence = await confirmReason({ title: code, reasonLabel: t('ops.residency.evidence') });
    if (!evidence) return;
    const country =
      verdict === 'approved'
        ? await confirmReason({ title: code, reasonLabel: t('ops.residency.country'), reasonMin: 2 })
        : null;
    if (verdict === 'approved' && !country) return;
    try {
      await decideResidency(code, { verdict, hostingCountry: country, evidence });
      data.reload();
      setError(undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-4">
      <Err message={error ?? data.error} />
      <p className="text-xs text-ink-muted">{t('ops.residency.note')}</p>

      <div
        className={`rounded p-3 text-sm ${
          data.data?.compliant ? 'bg-ok-bg text-ok' : 'bg-warn-bg text-warn'
        }`}
      >
        {data.data?.compliant
          ? t('ops.residency.compliant')
          : t('ops.residency.incomplete', { n: data.data?.pending ?? 0 })}
      </div>

      <Panel title={t('ops.residency.title')}>
        <Table>
          <thead className="text-ink-muted">
            <tr>
              <th className={TH}>{t('ops.residency.name')}</th>
              <th className={TH}>{t('ops.residency.category')}</th>
              <th className={TH}>{t('ops.residency.pii')}</th>
              <th className={TH}>{t('ops.residency.country')}</th>
              <th className={TH}>{t('ops.residency.verdict')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!data.data ? <SkeletonRows rows={4} columns={4} label={t('ops.loading')} /> : null}
            {(data.data?.services ?? []).map((s) => (
              <tr key={s.code} className="border-t border-line">
                <td className="py-1 pr-3">{s.name}</td>
                <td className="pr-3">{s.category}</td>
                <td className="pr-3">{s.processesPii ? '✓' : '—'}</td>
                <td className="pr-3">{s.hostingCountry ?? '—'}</td>
                <td
                  className={`pr-3 ${
                    s.verdict === 'approved'
                      ? 'text-ok'
                      : s.verdict === 'rejected'
                        ? 'text-danger'
                        : 'text-warn'
                  }`}
                >
                  {t(`ops.residency.${s.verdict}` as OpsKey)}
                </td>
                <td className="space-x-1 text-right">
                  <button className={GHOST} onClick={() => void decide(s.code, 'approved')}>
                    {t('ops.residency.approved')}
                  </button>
                  <button className={GHOST} onClick={() => void decide(s.code, 'rejected')}>
                    {t('ops.residency.rejected')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </div>
  );
}

export default function OpsModule({ user, siteId, title }: ModuleViewProps) {
  const t = opsStrings(user.locale);
  const [tab, setTab] = useTab<Tab>('ops', TABS, 'health');

  return (
    <ModulePage
      title={title}
      subtitle={t('ops.subtitle')}
      tabs={TABS.map((key) => ({ key, label: t(`ops.tab.${key}` as OpsKey) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'health' ? <HealthTab t={t} /> : null}
      {tab === 'devices' ? <DevicesTab t={t} user={user} siteId={siteId} /> : null}
      {tab === 'releases' ? <ReleasesTab t={t} /> : null}
      {tab === 'backups' ? <BackupsTab t={t} /> : null}
      {tab === 'residency' ? <ResidencyTab t={t} /> : null}
    </ModulePage>
  );
}
